import { useContext, useEffect, useState } from 'react';
import {
	Autocomplete,
	Box,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	Grid,
	InputLabel,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import FormsLayout from '../../../layout/FormsLayout';
import FormTextField from '../../UI/FormTextField';
import MainButton from '../../UI/MainButton';
import FormDropdown from '../../UI/FormDropdown';
import TextWithDivider from '../../UI/TextWithDivider';
import DividerWithButton from '../../UI/DividerWithButton';
import { getSpecializations } from '../../../api/user-api';
import { registerForeignStudent } from '../../../api/auth-api';
import SnackbarContext from '../../../store/snackbar-context';
import { loginPage } from '../../../store/fixed-routing';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import isEmail from 'validator/lib/isEmail';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import Groups2OutlinedIcon from '@mui/icons-material/Groups2Outlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const predefinedCountries = [
	'Litwa',
	'Łotwa',
	'Białoruś',
	'Ukraina',
	'Rumunia',
	'Niemcy',
	'Włochy',
	'Belgia',
	'Grecja',
	'Kanada',
	'Stany Zjednoczone',
	'Francja',
];

const RegisterForeignStudent = () => {
	const { palette } = useTheme();
	const { setMsg } = useContext(SnackbarContext);
	const minBirthDate = dayjs().subtract(21, 'year').startOf('day');
	const maxBirthDate = dayjs().subtract(8, 'year').endOf('day');
	const [specializations, setSpecializations] = useState<{ label: string; value: number }[]>([]);
	const [hasSecondName, setHasSecondName] = useState(false);
	const [validated, setValidated] = useState(false);
	const [sending, setSending] = useState(false);
	const [summaryOpen, setSummaryOpen] = useState(false);
	const [normalizationApplied, setNormalizationApplied] = useState(false);
	const [consents, setConsents] = useState({
		rodo: false,
		factual: false,
	});
	const [formInfo, setFormInfo] = useState({
		personalTitle: '',
		firstName: '',
		secondName: '',
		lastName: '',
		email: '',
		phone: '',
		password: '',
		passwordConfirm: '',
		classText: '',
		specializationLabel: '',
		specializationId: '',
		birthday: null as any,
		specialCertificate: false,
		country: '',
		city: '',
		street: '',
		building: '',
		apartment: '',
		zipCode: '',
		schoolName: '',
		schoolCity: '',
		schoolCountry: '',
		schoolStreet: '',
		schoolBuilding: '',
		schoolZipCode: '',
		teacherFirstName: '',
		teacherLastName: '',
		teacherEmail: '',
		teacherPhone: '',
		teacherAffiliation: '',
		teacherCity: '',
		teacherStreet: '',
		teacherBuilding: '',
		teacherApartment: '',
		teacherZipCode: '',
	});

	useEffect(() => {
		getSpecializations()
			.then((data) => {
				if (Array.isArray(data)) {
					const mapped = data.map((s: any) => ({ label: s.name, value: s.id }));
					setSpecializations(mapped);
					const bez = mapped.find((s) => s.label.trim().toLowerCase() === 'bez specjalizacji');
					if (bez) {
						setFormInfo((prev) =>
							prev.specializationId
								? prev
								: { ...prev, specializationLabel: bez.label, specializationId: String(bez.value) },
						);
					}
				}
			})
			.catch(() =>
				setMsg({ msg: 'Nie udało się pobrać listy specjalizacji. Prosimy odświeżyć stronę i spróbować ponownie.' }),
			);
	}, [setMsg]);

	const setField = (key: string, value: any) => {
		setFormInfo((prev: any) => ({ ...prev, [key]: value }));
	};

	const setStudentCountry = (value: string) => {
		setFormInfo((prev: any) => ({
			...prev,
			country: value,
			schoolCountry: prev.schoolCountry.trim() === '' ? value : prev.schoolCountry,
		}));
	};

	const validate = (): string | null => {
		if (!formInfo.personalTitle.trim()) return 'Prosimy o wybór tytułu ucznia (Pan/Pani).';
		if (!formInfo.firstName.trim()) return 'Prosimy o uzupełnienie imienia ucznia.';
		if (hasSecondName && !formInfo.secondName.trim()) return 'Prosimy o uzupełnienie drugiego imienia ucznia.';
		if (!formInfo.lastName.trim()) return 'Prosimy o uzupełnienie nazwiska ucznia.';
		if (
			formInfo.birthday &&
			(dayjs(formInfo.birthday).isBefore(minBirthDate, 'day') || dayjs(formInfo.birthday).isAfter(maxBirthDate, 'day'))
		) {
			return 'Data urodzenia ucznia musi wskazywać wiek od 8 do 21 lat.';
		}
		if (!formInfo.email.trim()) return 'Prosimy o uzupełnienie adresu e-mail ucznia.';
		if (!isEmail(formInfo.email.trim())) return 'Prosimy o podanie poprawnego adresu e-mail ucznia.';
		if (formInfo.password.length < 8) return 'Hasło musi mieć co najmniej 8 znaków.';
		if (formInfo.password !== formInfo.passwordConfirm) return 'Hasła muszą być identyczne.';
		if (!formInfo.classText.trim()) return 'Prosimy o uzupełnienie klasy (dowolny opis).';
		if (!formInfo.specializationId) {
			return 'Brak specjalizacji „Bez specjalizacji” w systemie albo nie udało się załadować listy. Prosimy odświeżyć stronę lub skontaktować się z administratorem.';
		}
		if (!formInfo.country.trim()) return 'Prosimy o uzupełnienie kraju ucznia.';
		if (!formInfo.city.trim()) return 'Prosimy o uzupełnienie miasta ucznia.';
		if (!formInfo.street.trim()) return 'Prosimy o uzupełnienie ulicy ucznia.';
		if (!formInfo.building.trim()) return 'Prosimy o uzupełnienie numeru budynku ucznia.';
		if (!formInfo.zipCode.trim()) return 'Prosimy o uzupełnienie kodu pocztowego ucznia.';
		if (!formInfo.schoolName.trim()) return 'Prosimy o uzupełnienie nazwy szkoły.';
		if (!formInfo.schoolCountry.trim()) return 'Prosimy o uzupełnienie kraju szkoły.';
		if (!formInfo.schoolCity.trim()) return 'Prosimy o uzupełnienie miasta szkoły.';
		if (formInfo.zipCode.trim().length > 15) return 'Kod pocztowy ucznia może mieć maksymalnie 15 znaków.';
		if (formInfo.schoolZipCode.trim().length > 15) return 'Kod pocztowy szkoły może mieć maksymalnie 15 znaków.';
		if (formInfo.teacherZipCode.trim().length > 15) return 'Kod pocztowy nauczyciela może mieć maksymalnie 15 znaków.';

		const hasTeacherName = formInfo.teacherFirstName.trim() || formInfo.teacherLastName.trim();
		if (hasTeacherName && (!formInfo.teacherFirstName.trim() || !formInfo.teacherLastName.trim())) {
			return 'Jeśli podajesz nauczyciela, prosimy o uzupełnienie imienia i nazwiska.';
		}
		if (formInfo.teacherEmail.trim() && !isEmail(formInfo.teacherEmail.trim())) {
			return 'Prosimy o podanie poprawnego adresu e-mail nauczyciela.';
		}
		if (!consents.factual) return 'Prosimy o potwierdzenie zgodności danych ze stanem faktycznym.';
		if (!consents.rodo) return 'Prosimy o zaznaczenie zgody RODO.';
		return null;
	};

	const getPoleWord = (count: number) => {
		if (count === 1) return 'pole';
		const mod10 = count % 10;
		const mod100 = count % 100;
		if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) {
			return 'pola';
		}
		return 'pól';
	};

	const buildSummarySections = (source = formInfo) => {
		const row = (label: string, value?: string | null) =>
			value && value.trim() !== '' ? { label, value: value.trim() } : null;
		const studentRows = [
			row('Tytuł', source.personalTitle),
			row('Imię', source.firstName),
			row('Drugie imię', source.secondName),
			row('Nazwisko', source.lastName),
			row('Data urodzenia', source.birthday ? dayjs(source.birthday).format('YYYY-MM-DD') : ''),
			row('Kraj', source.country),
			row('Miasto', source.city),
			row('Ulica', source.street),
			row('Numer budynku', source.building),
			row('Numer lokalu', source.apartment),
			row('Kod pocztowy', source.zipCode),
			row('Email', source.email),
			row('Telefon', source.phone),
			row('Klasa', source.classText),
		].filter(Boolean) as { label: string; value: string }[];
		const schoolRows = [
			row('Nazwa szkoły', source.schoolName),
			row('Kraj', source.schoolCountry),
			row('Miasto', source.schoolCity),
			row('Ulica', source.schoolStreet),
			row('Numer budynku', source.schoolBuilding),
			row('Kod pocztowy', source.schoolZipCode),
		].filter(Boolean) as { label: string; value: string }[];
		const teacherRows = [
			row('Imię', source.teacherFirstName),
			row('Nazwisko', source.teacherLastName),
			row('Email', source.teacherEmail),
			row('Telefon', source.teacherPhone),
			row('Afiliacja', source.teacherAffiliation),
		].filter(Boolean) as { label: string; value: string }[];
		const extraRows = [
			source.specialCertificate ? { label: 'Orzeczenie/opinia o dysgrafii', value: 'Tak' } : null,
		].filter(Boolean) as { label: string; value: string }[];
		return [
			{ title: 'Dane ucznia', rows: studentRows },
			{ title: 'Dane szkoły', rows: schoolRows },
			{ title: 'Dane nauczyciela', rows: teacherRows },
			{ title: 'Dodatkowe', rows: extraRows },
		].filter((section) => section.rows.length > 0);
	};

	const toSmartTitleCase = (value: string, forceRomanUppercase = false) => {
		const normalizeToken = (token: string) => {
			const lower = token.toLowerCase();
			const isRoman = /^[ivxlcdm]+$/i.test(token) && token.length <= 10;
			if (isRoman && forceRomanUppercase) {
				return token.toUpperCase();
			}
			if (!/[a-zA-Z]/.test(token)) {
				return token;
			}
			return lower.charAt(0).toUpperCase() + lower.slice(1);
		};
		return value
			.trim()
			.split(/\s+/)
			.map((word) =>
				word
					.split('-')
					.map((part) =>
						part
							.split("'")
							.map((subPart) => normalizeToken(subPart))
							.join("'"),
					)
					.join('-'),
			)
			.join(' ');
	};

	const buildNormalizedPayload = () => ({
		...formInfo,
		firstName: toSmartTitleCase(formInfo.firstName),
		secondName: toSmartTitleCase(formInfo.secondName),
		lastName: toSmartTitleCase(formInfo.lastName),
		country: toSmartTitleCase(formInfo.country),
		city: toSmartTitleCase(formInfo.city),
		street: toSmartTitleCase(formInfo.street, true),
		building: formInfo.building.trim(),
		apartment: formInfo.apartment.trim(),
		schoolName: toSmartTitleCase(formInfo.schoolName),
		schoolCity: toSmartTitleCase(formInfo.schoolCity),
		schoolCountry: toSmartTitleCase(formInfo.schoolCountry),
		schoolStreet: toSmartTitleCase(formInfo.schoolStreet, true),
		schoolBuilding: formInfo.schoolBuilding.trim(),
		teacherFirstName: toSmartTitleCase(formInfo.teacherFirstName),
		teacherLastName: toSmartTitleCase(formInfo.teacherLastName),
		teacherCity: toSmartTitleCase(formInfo.teacherCity),
		teacherStreet: toSmartTitleCase(formInfo.teacherStreet, true),
	});

	const submitRegistration = () => {
		if (sending) {
			return;
		}
		setSending(true);
		const { specializationLabel, ...payload } = formInfo;

		registerForeignStudent({
			...payload,
			birthday: formInfo.birthday ? dayjs(formInfo.birthday) : null,
		})
			.then((res: any) => {
				if (typeof res === 'string') {
					setMsg({ msg: res, severity: 'warning' });
					return;
				}
				if (res?.message) {
					setMsg({ msg: res.message, severity: 'success' });
				} else {
					setMsg({
						msg: 'Zgłoszenie zostało zapisane.',
						severity: 'success',
					});
				}
				setSummaryOpen(false);
				setFormInfo((prev) => ({
					...prev,
					personalTitle: '',
					firstName: '',
					secondName: '',
					lastName: '',
					email: '',
					phone: '',
					password: '',
					passwordConfirm: '',
					classText: '',
					specializationLabel: '',
					specializationId: '',
					birthday: null,
					specialCertificate: false,
					country: '',
					city: '',
					street: '',
					building: '',
					apartment: '',
					zipCode: '',
					schoolName: '',
					schoolCity: '',
					schoolCountry: '',
					schoolStreet: '',
					schoolBuilding: '',
					schoolZipCode: '',
					teacherFirstName: '',
					teacherLastName: '',
					teacherEmail: '',
					teacherPhone: '',
					teacherAffiliation: '',
					teacherCity: '',
					teacherStreet: '',
					teacherBuilding: '',
					teacherApartment: '',
					teacherZipCode: '',
				}));
				setNormalizationApplied(false);
				setHasSecondName(false);
				setConsents({
					rodo: false,
					factual: false,
				});
				setValidated(false);
			})
			.catch(() =>
				setMsg({ msg: 'Nie udało się zapisać zgłoszenia. Prosimy spróbować ponownie później.', severity: 'error' }),
			)
			.finally(() => setSending(false));
	};

	const actionSend = () => {
		setValidated(true);
		const error = validate();
		if (error) {
			setMsg({ msg: error, severity: 'warning' });
			return;
		}
		if (!normalizationApplied) {
			setFormInfo((prev) => ({ ...prev, ...buildNormalizedPayload() }));
			setNormalizationApplied(true);
		}
		setSummaryOpen(true);
	};
	const summarySections = buildSummarySections(formInfo);
	const sectionIconByTitle: Record<string, JSX.Element> = {
		'Dane ucznia': <PersonOutlineIcon fontSize='small' />,
		'Dane szkoły': <SchoolOutlinedIcon fontSize='small' />,
		'Dane nauczyciela': <Groups2OutlinedIcon fontSize='small' />,
		Dodatkowe: <InfoOutlinedIcon fontSize='small' />,
	};

	return (
		<FormsLayout>
			<Grid
				width={'100%'}
				maxWidth={'650px'}
				display={'flex'}
				flexDirection={'column'}
				gap={'24px'}
			>
				<Typography
					variant={'h3'}
					fontWeight={'500'}
					color={palette.primary.main}
				>
					Rejestracja ucznia z zagranicy
				</Typography>

				<Grid
					component={'form'}
					display={'flex'}
					flexDirection={'column'}
				>
					<Box
						display={'flex'}
						flexDirection={'column'}
						alignItems={'flex-start'}
						gap={'16px'}
						width={'100%'}
						mb={'16px'}
					>
						<TextWithDivider
							text={'Informacje o uczniu'}
							sx={{ mt: '8px', mb: '12px' }}
						/>
						<FormDropdown
							id={'foreign-title'}
							label={'Tytuł (Pan/Pani)'}
							validated={validated}
							errorMsg={!formInfo.personalTitle.trim() ? 'Pole wymagane' : ''}
							value={formInfo.personalTitle}
							values={['Pan', 'Pani']}
							valueChangeHandler={(v) => setField('personalTitle', v)}
						/>
						<FormTextField
							label='Imię'
							id='foreign-first-name'
							value={formInfo.firstName}
							valueChangeHandler={(v) => setField('firstName', v)}
							validated={validated}
							errorMsg={!formInfo.firstName.trim() ? 'Pole wymagane' : ''}
							maxLength={64}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={hasSecondName}
									onChange={(e) => {
										setHasSecondName(e.target.checked);
										if (!e.target.checked) {
											setField('secondName', '');
										}
									}}
								/>
							}
							label='Czy uczestnik posiada drugie imię?'
						/>
						{hasSecondName && (
							<FormTextField
								label='Drugie imię'
								id='foreign-second-name'
								value={formInfo.secondName}
								valueChangeHandler={(v) => setField('secondName', v)}
								validated={validated}
								errorMsg={!formInfo.secondName.trim() ? 'Pole wymagane' : ''}
								maxLength={64}
							/>
						)}
						<FormTextField
							label='Nazwisko'
							id='foreign-last-name'
							value={formInfo.lastName}
							valueChangeHandler={(v) => setField('lastName', v)}
							validated={validated}
							errorMsg={!formInfo.lastName.trim() ? 'Pole wymagane' : ''}
							maxLength={64}
						/>
						<Box sx={{ width: '100%' }}>
							<LocalizationProvider
								dateAdapter={AdapterDayjs}
								adapterLocale='pl'
							>
								<InputLabel sx={{ mb: '4px' }}>Data urodzenia (opcjonalnie)</InputLabel>
								<DatePicker
									label=''
									value={formInfo.birthday}
									minDate={minBirthDate}
									maxDate={maxBirthDate}
									onChange={(newValue) => setField('birthday', newValue)}
									sx={{ width: '100%' }}
								/>
							</LocalizationProvider>
						</Box>
						<Grid
							container
							flexDirection={'column'}
							width={'100%'}
							gap={'4px'}
						>
							<InputLabel htmlFor='foreign-country'>Kraj ucznia</InputLabel>
							<Autocomplete
								id='foreign-country'
								freeSolo
								options={predefinedCountries}
								value={formInfo.country}
								onInputChange={(_e, value) => setStudentCountry(value)}
								onChange={(_e, value) => setStudentCountry(value || '')}
								renderInput={(params) => (
									<TextField
										{...params}
										size='small'
										error={validated && !formInfo.country.trim()}
										inputProps={{ ...params.inputProps, maxLength: 100 }}
									/>
								)}
							/>
						</Grid>
						<FormTextField
							label='Adres zamieszkania/korespondencyjny'
							id='foreign-street'
							placeholder='Ulica'
							value={formInfo.street}
							valueChangeHandler={(v) => setField('street', v)}
							validated={validated}
							errorMsg={!formInfo.street.trim() ? 'Pole wymagane' : ''}
							maxLength={100}
						/>
						<FormTextField
							label=''
							id='foreign-building'
							placeholder='Numer budynku'
							value={formInfo.building}
							valueChangeHandler={(v) => setField('building', v)}
							validated={validated}
							errorMsg={!formInfo.building.trim() ? 'Pole wymagane' : ''}
							maxLength={10}
						/>
						<FormTextField
							label=''
							id='foreign-apartment'
							placeholder='Numer mieszkania (opcjonalnie)'
							value={formInfo.apartment}
							valueChangeHandler={(v) => setField('apartment', v)}
							validated={false}
							errorMsg=''
							maxLength={10}
						/>
						<Box
							display='flex'
							width='100%'
							gap={2}
							alignItems='flex-start'
						>
							<Box flex={1}>
								<FormTextField
									label=''
									id='foreign-zip'
									placeholder='Kod pocztowy (max 15)'
									value={formInfo.zipCode}
									valueChangeHandler={(v) => setField('zipCode', v)}
									validated={validated}
									errorMsg={
										!formInfo.zipCode.trim()
											? 'Pole wymagane'
											: formInfo.zipCode.trim().length > 15
												? 'Maksymalnie 15 znaków'
												: ''
									}
									maxLength={15}
								/>
							</Box>
							<Box flex={1}>
								<FormTextField
									label=''
									id='foreign-city'
									placeholder='Miasto'
									value={formInfo.city}
									valueChangeHandler={(v) => setField('city', v)}
									validated={validated}
									errorMsg={!formInfo.city.trim() ? 'Pole wymagane' : ''}
									maxLength={100}
								/>
							</Box>
						</Box>
						<FormTextField
							label='Email'
							id='foreign-email'
							type='email'
							value={formInfo.email}
							valueChangeHandler={(v) => setField('email', v)}
							validated={validated}
							errorMsg={!formInfo.email.trim() ? 'Pole wymagane' : ''}
							maxLength={120}
						/>
						<FormTextField
							label='Telefon (opcjonalnie)'
							id='foreign-phone'
							value={formInfo.phone}
							valueChangeHandler={(v) => setField('phone', v)}
							validated={false}
							errorMsg=''
							maxLength={20}
						/>
						<FormTextField
							label='Hasło'
							id='foreign-password'
							type='password'
							value={formInfo.password}
							valueChangeHandler={(v) => setField('password', v)}
							validated={validated}
							errorMsg={formInfo.password.length > 0 && formInfo.password.length < 8 ? 'Min. 8 znaków' : ''}
							maxLength={128}
						/>
						<FormTextField
							label='Powtórz hasło'
							id='foreign-password-confirm'
							type='password'
							value={formInfo.passwordConfirm}
							valueChangeHandler={(v) => setField('passwordConfirm', v)}
							validated={validated}
							errorMsg={
								formInfo.passwordConfirm.length > 0 && formInfo.passwordConfirm !== formInfo.password
									? 'Hasła muszą być identyczne'
									: ''
							}
							maxLength={128}
						/>
						<FormTextField
							label='Klasa (wpis ręczny)'
							id='foreign-class-text'
							value={formInfo.classText}
							valueChangeHandler={(v) => setField('classText', v)}
							validated={validated}
							errorMsg={!formInfo.classText.trim() ? 'Pole wymagane' : ''}
							maxLength={40}
						/>
						<TextWithDivider
							text={'Informacje o szkole'}
							dividerColor={palette.secondary.main}
							sx={{ mt: '24px', mb: '12px' }}
						/>
						<Typography
							variant='body2'
							sx={{ color: palette.primary.main, opacity: 0.85, mb: 1, lineHeight: 1.4 }}
						>
							Prosimy o podanie nazwy szkoły, do której uczestnik uczęszcza, ewentualnie szkoły, w której uczy się
							języka polskiego. Podana nazwa szkoły znajdzie się na zaświadczeniu finalisty/laureata.
						</Typography>
						<FormTextField
							label='Nazwa szkoły'
							id='foreign-school-name'
							value={formInfo.schoolName}
							valueChangeHandler={(v) => setField('schoolName', v)}
							validated={validated}
							errorMsg={!formInfo.schoolName.trim() ? 'Pole wymagane' : ''}
							maxLength={255}
						/>
						<Grid
							container
							flexDirection={'column'}
							width={'100%'}
							gap={'4px'}
						>
							<InputLabel htmlFor='foreign-school-country'>Kraj szkoły</InputLabel>
							<Autocomplete
								id='foreign-school-country'
								freeSolo
								options={predefinedCountries}
								value={formInfo.schoolCountry}
								onInputChange={(_e, value) => setField('schoolCountry', value)}
								onChange={(_e, value) => setField('schoolCountry', value || '')}
								renderInput={(params) => (
									<TextField
										{...params}
										size='small'
										error={validated && !formInfo.schoolCountry.trim()}
										inputProps={{ ...params.inputProps, maxLength: 100 }}
									/>
								)}
							/>
						</Grid>
						<FormTextField
							label='Adres szkoły'
							id='foreign-school-street'
							value={formInfo.schoolStreet}
							placeholder='Ulica (opcjonalnie)'
							valueChangeHandler={(v) => setField('schoolStreet', v)}
							validated={false}
							errorMsg=''
							maxLength={100}
						/>
						<FormTextField
							label=''
							id='foreign-school-building'
							value={formInfo.schoolBuilding}
							placeholder='Numer budynku (opcjonalnie)'
							valueChangeHandler={(v) => setField('schoolBuilding', v)}
							validated={false}
							errorMsg=''
							maxLength={10}
						/>
						<Box
							display='flex'
							width='100%'
							gap={2}
							alignItems='flex-start'
						>
							<Box flex={1}>
								<FormTextField
									label=''
									id='foreign-school-zip'
									value={formInfo.schoolZipCode}
									placeholder='Kod pocztowy (opcjonalnie, max 15)'
									valueChangeHandler={(v) => setField('schoolZipCode', v)}
									validated={validated}
									errorMsg={formInfo.schoolZipCode.trim().length > 15 ? 'Maksymalnie 15 znaków' : ''}
									maxLength={15}
								/>
							</Box>
							<Box flex={1}>
								<FormTextField
									label=''
									id='foreign-school-city'
									value={formInfo.schoolCity}
									placeholder='Miasto'
									valueChangeHandler={(v) => setField('schoolCity', v)}
									validated={validated}
									errorMsg={!formInfo.schoolCity.trim() ? 'Pole wymagane' : ''}
									maxLength={100}
								/>
							</Box>
						</Box>
						<TextWithDivider
							text={'Informacje o nauczycielu (opcjonalnie)'}
							dividerColor={palette.secondary.main}
							sx={{ mt: '24px', mb: '12px' }}
						/>
						<FormTextField
							label='Imię nauczyciela'
							id='foreign-teacher-first-name'
							value={formInfo.teacherFirstName}
							valueChangeHandler={(v) => setField('teacherFirstName', v)}
							validated={validated}
							errorMsg={formInfo.teacherLastName.trim() && !formInfo.teacherFirstName.trim() ? 'Uzupełnij imię' : ''}
							maxLength={64}
						/>
						<FormTextField
							label='Nazwisko nauczyciela'
							id='foreign-teacher-last-name'
							value={formInfo.teacherLastName}
							valueChangeHandler={(v) => setField('teacherLastName', v)}
							validated={validated}
							errorMsg={
								formInfo.teacherFirstName.trim() && !formInfo.teacherLastName.trim() ? 'Uzupełnij nazwisko' : ''
							}
							maxLength={64}
						/>
						<FormTextField
							label='Email nauczyciela (opcjonalnie)'
							id='foreign-teacher-email'
							type='email'
							value={formInfo.teacherEmail}
							valueChangeHandler={(v) => setField('teacherEmail', v)}
							validated={false}
							errorMsg=''
							maxLength={120}
						/>
						<FormTextField
							label='Telefon nauczyciela (opcjonalnie)'
							id='foreign-teacher-phone'
							value={formInfo.teacherPhone}
							valueChangeHandler={(v) => setField('teacherPhone', v)}
							validated={false}
							errorMsg=''
							maxLength={20}
						/>
						<FormTextField
							label='Afiliacja nauczyciela (opcjonalnie)'
							id='foreign-teacher-affiliation'
							value={formInfo.teacherAffiliation}
							valueChangeHandler={(v) => setField('teacherAffiliation', v)}
							validated={false}
							errorMsg=''
							maxLength={255}
						/>
						<TextWithDivider
							text={'Dodatkowe'}
							dividerColor={palette.secondary.light}
							sx={{ mt: '24px', mb: '8px' }}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={!!formInfo.specialCertificate}
									onChange={(e) => setField('specialCertificate', e.target.checked)}
								/>
							}
							label='Uczeń posiada orzeczenie/opinię o dysgrafii'
						/>
						<TextWithDivider
							text={'Zgody'}
							dividerColor={palette.secondary.light}
							sx={{ mt: '16px', mb: '8px' }}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={consents.rodo}
									onChange={(e) => setConsents((prev) => ({ ...prev, rodo: e.target.checked }))}
								/>
							}
							label='Zgoda RODO'
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={consents.factual}
									onChange={(e) => setConsents((prev) => ({ ...prev, factual: e.target.checked }))}
								/>
							}
							label='Oświadczam, że powyższe dane są zgodne ze stanem faktycznym'
						/>
					</Box>
				</Grid>
				<Box
					mt={'8px'}
					mb={'32px'}
					alignSelf={'center'}
					display={'flex'}
					justifyContent={'center'}
					alignItems={'center'}
					position={'relative'}
				>
					<MainButton
						title={'Wyślij zgłoszenie'}
						darkVersion
						onClick={actionSend}
					/>
					<CircularProgress sx={{ display: sending ? 'block' : 'none', position: 'absolute', right: '-60px' }} />
				</Box>
			</Grid>
			<Box mt={'64px'}>
				<DividerWithButton
					label={'Masz już konto?'}
					hrefPath={loginPage}
					buttonTitle={'Zaloguj się'}
				/>
			</Box>
			<Dialog
				open={summaryOpen}
				onClose={() => {
					if (!sending) {
						setSummaryOpen(false);
					}
				}}
				fullWidth
				maxWidth='md'
			>
				<DialogTitle sx={{ pb: 1 }}>
					<Box
						display='flex'
						alignItems='center'
						gap={1}
					>
						<CheckCircleOutlineIcon sx={{ color: palette.primary.main }} />
						<Typography
							variant='h5'
							color={palette.primary.main}
						>
							Sprawdź dane przed wysłaniem
						</Typography>
					</Box>
				</DialogTitle>
				<DialogContent sx={{ pt: 1 }}>
					<Box
						sx={{
							mb: 2,
							px: 1.5,
							py: 1,
							borderRadius: 2,
							backgroundColor: palette.primary.light,
							border: `1px solid ${palette.primary.main}`,
						}}
					>
						<Typography
							variant='body2'
							sx={{ color: palette.primary.main }}
						>
							Prosimy o potwierdzenie poprawności danych. W przypadku konieczności wprowadzenia zmian prosimy wybrać{' '}
							<b>Wróć do edycji</b>.
						</Typography>
					</Box>
					<Box
						display='flex'
						flexDirection='column'
						gap={2}
					>
						{summarySections.map((section, sectionIdx) => (
							<Box
								key={section.title}
								sx={{
									border: `1px solid ${palette.primary.light}`,
									borderRadius: 3,
									p: { xs: 1.5, md: 2 },
									boxShadow: '0 3px 10px rgba(0,0,0,0.06)',
									backgroundColor: '#fff',
								}}
							>
								<Box
									display='flex'
									alignItems='center'
									justifyContent='space-between'
									mb={1}
								>
									<Box
										display='flex'
										alignItems='center'
										gap={1}
									>
										<Box sx={{ color: palette.primary.main, display: 'flex', alignItems: 'center' }}>
											{sectionIconByTitle[section.title]}
										</Box>
										<Typography
											variant='h6'
											sx={{ color: palette.primary.main, fontSize: { xs: '1rem', md: '1.1rem' } }}
										>
											{section.title}
										</Typography>
									</Box>
									<Chip
										size='small'
										label={`${section.rows.length} ${getPoleWord(section.rows.length)}`}
										sx={{ color: palette.primary.main, borderColor: palette.primary.main }}
										variant='outlined'
									/>
								</Box>
								{section.rows.map((item) => (
									<Grid
										key={`${section.title}-${item.label}`}
										container
										alignItems='center'
										sx={{
											display: 'grid',
											gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 32%) 1fr' },
											py: 0.75,
											columnGap: 2,
											rowGap: 0.8,
											borderBottom: `1px dashed ${palette.primary.light}`,
											'&:last-of-type': { borderBottom: 'none' },
										}}
									>
										<Typography
											variant='body1'
											sx={{ color: palette.primary.main, fontWeight: 700, textAlign: 'left', minWidth: 'fit-content' }}
										>
											{item.label}
										</Typography>
										<Box
											sx={{
												flex: 1,
												pl: { xs: 0, md: 2 },
												py: 0.4,
												px: 1.2,
												borderRadius: 1.5,
												backgroundColor: 'rgba(0, 83, 160, 0.06)',
											}}
										>
											<Typography
												variant='body1'
												sx={{
													color: palette.primary.main,
													textAlign: 'left',
													wordBreak: 'break-word',
												}}
											>
												{item.value}
											</Typography>
										</Box>
									</Grid>
								))}
								{sectionIdx < summarySections.length - 1 && <Divider sx={{ mt: 1 }} />}
							</Box>
						))}
					</Box>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, pt: 1, justifyContent: 'space-between' }}>
					<MainButton
						title='Wróć do edycji'
						onClick={() => setSummaryOpen(false)}
						sx={{ width: 'auto', px: 3 }}
					/>
					<MainButton
						title={sending ? 'Wysyłanie...' : 'Potwierdzam i wysyłam'}
						darkVersion
						onClick={submitRegistration}
						sx={{ width: 'auto', px: 3 }}
					/>
				</DialogActions>
			</Dialog>
		</FormsLayout>
	);
};

export default RegisterForeignStudent;
